<template>
  <div>
    <page-header
      :title="'Assignment Submission Details'"
      :container-class="containerClass"
      :show-back="true"
      class="mb-32pt"
    />

    <div class="page-section">
      <div :class="containerClass">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner class="align-middle mr-2"></b-spinner>
          <strong>Loading submission...</strong>
        </div>

        <!-- Submission Details -->
        <div v-else-if="submission">
          <!-- Assignment Info Card -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ submission.assignment.title }}</h5>
                <b-badge :variant="getStatusVariant(submission.status)" pill class="px-3 py-2">
                  <i class="material-icons icon-16pt mr-1">{{ getStatusIcon(submission.status) }}</i>
                  {{ formatStatus(submission.status) }}
                </b-badge>
              </div>
              <p class="text-muted mb-0 mt-2" v-if="submission.assignment.description">
                {{ submission.assignment.description }}
              </p>
            </div>
            <div class="card-body py-3">
              <div class="row">
                <div class="col-md-3" v-if="submission.assignment.program">
                  <div class="info-item">
                    <i class="material-icons text-primary mr-2">school</i>
                    <div>
                      <small class="text-muted d-block">Program</small>
                      <strong>{{ submission.assignment.program.title }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-info mr-2">event</i>
                    <div>
                      <small class="text-muted d-block">Started</small>
                      <strong>{{ formatDate(submission.start_time) }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-warning mr-2">assignment</i>
                    <div>
                      <small class="text-muted d-block">Tasks Completed</small>
                      <strong>{{ submission.completed_tasks }} / {{ submission.total_tasks }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-success mr-2">trending_up</i>
                    <div>
                      <small class="text-muted d-block">Progress</small>
                      <strong>{{ submission.progress }}%</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- View All Attempts Button -->
          <div class="mb-3 text-right">
            <b-btn
              variant="primary"
              @click="viewAllAttempts"
              :style="{ backgroundColor: primaryColor, borderColor: primaryColor }"
            >
              <i class="material-icons icon-16pt mr-1">visibility</i>
              View All Attempts
            </b-btn>
          </div>

          <!-- Assignment Metadata -->
          <div class="card mb-3">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="material-icons icon-16pt mr-2">info</i>
                Assignment Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4">
                  <div class="meta-item">
                    <span class="meta-label">Total Tasks:</span>
                    <span class="meta-value">{{ submission.assignment.total_tasks }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="meta-item">
                    <span class="meta-label">Estimated Time:</span>
                    <span class="meta-value">{{ submission.assignment.estimated_time_minutes }} minutes</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="meta-item">
                    <span class="meta-label">Max Attempts:</span>
                    <span class="meta-value">{{ submission.max_attempts || submission.assignment.max_attempts }}</span>
                  </div>
                </div>
                <div class="col-md-4" v-if="submission.assignment.total_marks">
                  <div class="meta-item">
                    <span class="meta-label">Total Marks:</span>
                    <span class="meta-value">{{ submission.assignment.total_marks }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="meta-item">
                    <span class="meta-label">Attempts Used:</span>
                    <span class="meta-value">{{ submission.attempts }}</span>
                  </div>
                </div>
                <div class="col-md-4" v-if="submission.end_time">
                  <div class="meta-item">
                    <span class="meta-label">Completed At:</span>
                    <span class="meta-value">{{ formatDate(submission.end_time) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Task Submissions -->
          <div v-if="submission.extras && submission.extras.tasks" class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="material-icons icon-16pt mr-2">list_alt</i>
                Task Submissions
              </h5>
            </div>
            <div class="card-body">
              <div v-for="(task, taskKey, index) in submission.extras.tasks" :key="taskKey" class="task-item">
                <div class="task-card">
                  <div class="task-header">
                    <div class="d-flex align-items-start justify-content-between">
                      <div class="d-flex align-items-start flex-grow-1">
                        <div class="task-number" :style="{ backgroundColor: primaryColor }">{{ index + 1 }}</div>
                        <div class="flex-grow-1">
                          <h6 class="mb-2">{{ task.title }}</h6>
                          <b-badge :variant="getTaskStatusVariant(task.status)" class="mr-2">
                            <i class="material-icons icon-12pt">{{ getTaskStatusIcon(task.status) }}</i>
                            {{ formatStatus(task.status) }}
                          </b-badge>
                          <span class="task-type-badge">{{ getTaskTypeLabel(task.type) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="task-body" v-if="task.evidence">
                    <!-- Upload type -->
                    <div v-if="task.type === 'upload'">
                      <div v-if="task.evidence.file" class="file-attachment">
                        <div class="file-label">
                          <i class="fas fa-paperclip mr-2"></i>Submitted File
                        </div>
                        <a
                          :href="task.evidence.file"
                          target="_blank"
                          rel="noopener"
                          download
                          class="download-btn"
                          :style="{ backgroundColor: primaryColor }"
                        >
                          <i class="fas fa-download mr-2"></i>Download File
                        </a>
                      </div>
                      <div v-if="task.evidence.note" class="submission-note mt-3">
                        <div class="note-label">
                          <i class="fas fa-comment-alt mr-2"></i>Additional Note
                        </div>
                        <div class="note-content" v-safe-html="task.evidence.note"></div>
                      </div>
                    </div>

                    <!-- Text type -->
                    <div v-else-if="task.type === 'text'">
                      <div v-if="task.evidence.note" class="text-submission">
                        <div class="submission-label">
                          <i class="fas fa-file-alt mr-2"></i>Student's Response
                        </div>
                        <div class="text-content" v-safe-html="task.evidence.note"></div>
                      </div>
                    </div>

                    <!-- Case Study type -->
                    <div v-else-if="task.type === 'case_study'">
                      <div v-if="task.evidence.note" class="text-submission">
                        <div class="submission-label">
                          <i class="fas fa-book-reader mr-2"></i>Case Study Response
                        </div>
                        <div class="text-content" v-safe-html="task.evidence.note"></div>
                      </div>
                    </div>

                    <!-- Scenario interview: what the student actually said -->
                    <div v-else-if="task.type === 'scenario'">
                      <div class="text-submission">
                        <div class="submission-label">
                          <i class="fas fa-microphone-alt mr-2"></i>Interview Transcript
                        </div>
                        <div class="interview-meta mb-2">
                          <span v-if="interviewFor(taskKey).simulation_title">
                            {{ interviewFor(taskKey).simulation_title }}
                          </span>
                          <span v-if="interviewFor(taskKey).call_duration">
                            &middot; {{ formatDuration(interviewFor(taskKey).call_duration) }}
                          </span>
                        </div>
                        <div v-if="interviewFor(taskKey).call_summary" class="interview-summary mb-2">
                          <strong>Summary:</strong> {{ interviewFor(taskKey).call_summary }}
                        </div>
                        <pre v-if="interviewFor(taskKey).transcript" class="transcript-content">{{ interviewFor(taskKey).transcript }}</pre>
                        <div v-else class="text-muted font-italic">
                          No transcript was recorded for this interview.
                        </div>
                      </div>
                    </div>

                    <!-- Checkbox type -->
                    <div v-else class="checkbox-section">
                      <div v-if="String(task.status).toLowerCase() === 'done'" class="checkbox-completed">
                        <i class="fas fa-check-square mr-2"></i>Task marked as completed
                      </div>
                      <div v-else class="checkbox-pending">
                        <i class="far fa-square mr-2"></i>Task not yet completed
                      </div>
                    </div>
                  </div>

                  <div v-else class="task-body">
                    <div class="no-submission">
                      <i class="fas fa-info-circle mr-2"></i>No submission for this task
                    </div>
                  </div>
                </div>
                <hr v-if="index < Object.keys(submission.extras.tasks).length - 1" class="my-3">
              </div>
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else class="text-center py-5">
          <p class="text-danger">Failed to load submission details.</p>
          <b-btn variant="primary" @click="fetchSubmissionDetails">Retry</b-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { get, startCase } from 'lodash';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: {
    PageHeader,
  },
  extends: Page,

  data() {
    return {
      title: 'Simple Submission Detail',
      isLoading: true,
      submission: null,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    primaryColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color', '#3b82f6');
    },
  },

  methods: {
    get,

    interviewFor(taskId) {
      return (this.submission && this.submission.scenario_interviews
        && this.submission.scenario_interviews[taskId]) || {};
    },

    formatDuration(seconds) {
      const total = Math.round(Number(seconds) || 0);
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      return `${mins}m ${String(secs).padStart(2, '0')}s`;
    },

    async fetchSubmissionDetails() {
      this.isLoading = true;
      try {
        const response = await axios.get(`api/lms/assignment-submissions/${this.$route.params.id}/`);
        this.submission = response.data.data || response.data;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch submission details' });
        this.submission = null;
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatStatus(status) {
      if (!status) return '-';
      const statusMap = {
        completed: 'Completed',
        in_progress: 'In Progress',
        pending: 'Pending',
        done: 'Done',
      };
      const normalized = String(status).toLowerCase();
      return statusMap[normalized] || startCase(normalized.replace(/[-_]/g, ' '));
    },

    getStatusVariant(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'completed' || normalized === 'done') return 'success';
      if (normalized === 'in_progress') return 'info';
      return 'warning';
    },

    getStatusIcon(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'completed' || normalized === 'done') return 'check_circle';
      if (normalized === 'in_progress') return 'pending';
      return 'schedule';
    },

    getTaskStatusVariant(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'done' || normalized === 'completed') return 'success';
      return 'secondary';
    },

    getTaskStatusIcon(status) {
      const normalized = String(status).toLowerCase();
      if (normalized === 'done' || normalized === 'completed') return 'check';
      return 'close';
    },

    getTaskTypeLabel(type) {
      const map = {
        upload: 'File Upload',
        text: 'Text Response',
        case_study: 'Case Study',
        check: 'Checkbox',
        checkbox: 'Checkbox',
      };
      return map[type] || startCase(type);
    },

    viewAllAttempts() {
      if (!this.submission || !this.submission.assignment) return;
      
      // Get student_id from query params or submission
      const studentId = this.$route.query.student_id || this.submission.student_id || this.submission.student;
      const tab = this.$route.query.tab;
      
      this.$router.push({
        name: 'assignment-attempts',
        params: {
          enrollmentId: 'simple-submissions',
          assignmentId: this.submission.assignment.id,
        },
        query: {
          student_id: studentId,
          ...(tab !== undefined && { tab }),
        },
      });
    },
  },

  created() {
    this.fetchSubmissionDetails();
  },
};
</script>

<style scoped>
.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.info-item i {
  font-size: 20px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.meta-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.meta-value {
  font-weight: 600;
  color: #212529;
}

.task-item {
  padding: 0.5rem 0;
}

.task-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.task-header {
  background: #fafbfc;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.task-number {
  width: 32px;
  height: 32px;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 1rem;
  flex-shrink: 0;
  font-size: 0.875rem;
}

.task-type-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: #f3f4f6;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
}

.task-body {
  padding: 1.25rem;
}

.file-attachment {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
}

.file-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  color: #ffffff;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.8125rem;
  text-decoration: none;
  transition: all 0.15s ease;
}

.download-btn:hover {
  opacity: 0.9;
  color: #ffffff;
  text-decoration: none;
}

.submission-note {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
}

.note-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.note-content {
  color: #1e293b;
  line-height: 1.6;
}

.text-submission {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  padding: 1rem;
}

.submission-label {
  font-weight: 600;
  color: #0369a1;
  font-size: 0.8125rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.text-content {
  color: #1e293b;
  line-height: 1.6;
}

.interview-meta {
  color: #64748b;
  font-size: 0.8125rem;
}

.interview-summary {
  color: #1e293b;
  font-size: 0.875rem;
}

.transcript-content {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0.75rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.875rem;
  color: #1e293b;
  margin: 0;
}

.checkbox-section {
  padding: 0.5rem 0;
}

.checkbox-completed {
  color: #059669;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.checkbox-pending {
  color: #6b7280;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.no-submission {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  color: #6b7280;
  font-style: italic;
  display: flex;
  align-items: center;
}

.icon-12pt {
  font-size: 12pt;
  vertical-align: middle;
}
</style>
